import { ChangeEvent, useRef, useState } from "react"

import { CellRendererProps, useCell } from "components/Bable"
import { css, cx } from "@emotion/css"
import { formatFloatingPointNumber, parseNumber } from "utils/number-format"

import { ReactComponent as DeleteIcon } from "assets/delete.svg"
import { Fee } from "models/fees"
import GhostTextInput from "ui/inputs/GhostTextInput"
import MoreActions from "components/MoreActions"
import { MoreButtonBlock } from "components/buttons/MoreButtonBlock"
import { MultiSelect } from "routes/Base/Admin/components/MultiSelect"
import Options from "components/inputs/Options"
import { TextInput } from "components/inputs/TextInput"
import { useAuthenticated } from "providers/A12n"
import { globalState, useContextOptions } from "."
import { useI18n } from "providers/I18n"
import { useRifm } from "rifm"
import { useRow } from "components/Bable/data/useRow"
import { useMutation } from "react-query"
import { patchFee } from "api/fees"
import { ApiError } from "api/http/utils"
import { isEqual } from "lodash"

const useUpdate = () => {
  const [patch] = useMutation(patchFee, {
    onSuccess: (f: Fee) => globalState.updateRow(f),
    onError: (e: ApiError, mf: Partial<Fee>) => {
      const error = e as ApiError
      const errorMap = error?.errorsMap ?? {}
      !!mf.id && globalState.setRowErrors(mf.id, errorMap)
    },
  })
  return patch
}

const EditableNumberCell = (
  props: CellRendererProps & { disabled?: boolean },
) => {
  const { dataKey, rowId, disabled } = props
  const { onUpdate } = useContextOptions()
  const [value, setValue, error] = useCell<Fee>(rowId, dataKey as keyof Fee, "")
  const row = useRow<Fee>(rowId)
  let old = useRef(value)

  const patch = useUpdate()

  const rifm = useRifm({
    value: !!value ? `${value}` : "",
    accept: /[\d.]/g,
    onChange: (v) => setValue(parseNumber(v)),
    format: (v) => (!!v ? formatFloatingPointNumber()(v) : ""),
  })

  const onBlur = () => {
    const hasValueDiff = old.current !== value
    if (dataKey !== undefined && hasValueDiff) {
      onUpdate(row.value, patch, dataKey, value).then(
        () => (old.current = value),
      )
    }
  }

  return !disabled ? (
    <TextInput
      value={rifm.value}
      onChange={rifm.onChange}
      onBlur={onBlur}
      className={cx(styles.input, styles.ghostSelect, {
        [styles.error]: !!error,
        [styles.prcInput]: row.get()?.amountType === "PERCENTAGE",
      })}
      InputProps={{
        endAdornment: row.get()?.amountType === "PERCENTAGE" ? "%" : "",
      }}
    />
  ) : (
    <div className={cx("BodyBody-2BlackRegular", styles.readonly)}>
      {rifm.value}
      {row.get()?.amountType === "PERCENTAGE" && !!rifm.value ? "%" : ""}
    </div>
  )
}

const EditableCell = (props: CellRendererProps & { disabled?: boolean }) => {
  const { dataKey, rowId, disabled } = props
  const { onUpdate } = useContextOptions()
  const [value, setValue, error] = useCell<Fee>(rowId, dataKey as keyof Fee, "")
  const row = useRow<Fee>(rowId)
  let old = useRef(value)

  const patch = useUpdate()

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value)
  }
  const onBlur = () => {
    const hasValueDiff = old.current !== value
    if (dataKey !== undefined && hasValueDiff) {
      onUpdate(row.value, patch, dataKey, value).then(
        () => (old.current = value),
      )
    }
  }

  return !disabled ? (
    <GhostTextInput
      value={value as string}
      onChange={onChange}
      onBlur={onBlur}
      className={cx(styles.input, styles.ghostInput, {
        [styles.error]: !!error,
      })}
    />
  ) : (
    <div className={cx("BodyBody-2BlackRegular", styles.readonly)}>{value}</div>
  )
}

const SelectCell = (
  props: CellRendererProps & {
    isScopeVendor?: boolean
    disabled?: boolean
  },
) => {
  const { t } = useI18n()
  const { dataKey, rowId, disabled } = props

  const { onUpdate, feesSemanticsOptions } = useContextOptions()
  const [value, setValue, error] = useCell<Fee>(rowId, dataKey as keyof Fee, "")
  const row = useRow<Fee>(rowId)
  let old = useRef(value)

  const patch = useUpdate()

  const typesOptions = [
    { label: t("fee.amount.FIXED_AMOUNT"), value: "FIXED_AMOUNT" },
    { label: t("fee.amount.PERCENTAGE"), value: "PERCENTAGE" },
  ]

  const computedOnOptions = [
    { label: t("fee.computedOn.GROSS"), value: "GROSS" },
    { label: t("fee.computedOn.NET"), value: "NET" },
  ]

  const handleChange = (
    evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = evt.target
    if (value) {
      setValue(value)
    }
  }

  const onBlur = () => {
    const hasValueDiff = !isEqual(old.current, value)
    if ((dataKey !== undefined && !!value) || hasValueDiff) {
      onUpdate(row.value, patch, dataKey, value).then(
        () => (old.current = value),
      )
    }
  }

  let cellOptions = [] as any[]

  switch (dataKey) {
    case "semantic":
      cellOptions = feesSemanticsOptions || []
      break
    case "amountType":
      cellOptions = typesOptions || []
      break
    case "computedOn":
      cellOptions = computedOnOptions || []
      break
    default:
      cellOptions = []
  }

  return !disabled ? (
    <div className={styles.buttonContainer}>
      <Options
        name={dataKey || ""}
        value={value}
        options={cellOptions}
        onChange={handleChange}
        onBlur={onBlur}
        className={cx(styles.ghostSelect, {
          [styles.error]: !!error,
        })}
        size="medium"
      />
    </div>
  ) : (
    <div className={cx("BodyBody-2BlackRegular", styles.readonly)}>{value}</div>
  )
}

const MultipleSelectCell = (
  props: CellRendererProps & {
    disabled?: boolean
  },
) => {
  const { t } = useI18n()
  const { dataKey, rowId, disabled } = props
  const {
    mediaSemanticOptions,
    vendorProductsOptions,
    onUpdate,
    hasMediaTypes,
  } = useContextOptions()
  const [value, setValue, error] = useCell<Fee>(rowId, dataKey as keyof Fee, [])
  const row = useRow<Fee>(rowId)
  const [open, setOpen] = useState<boolean>(false)
  const { hasAuthority } = useAuthenticated()
  let old = useRef(value)

  const patch = useUpdate()

  const onBlur = () => {
    const hasValueDiff = !isEqual(old.current, value)
    hasValueDiff &&
      onUpdate(row.value, patch, dataKey, value).then(
        () => (old.current = value),
      )
    setOpen(false)
  }

  const onOpen = () => {
    setOpen(true)
  }

  const handleToggleOption = (selectedOptions: Array<any>) => {
    const _v = selectedOptions.map((x) => x.value)
    setValue(_v)
  }

  const handleClearOptions = () => setValue([])

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      setValue(cellOptions?.map((o) => o?.value))
    } else {
      handleClearOptions()
    }
  }

  let cellOptions = [] as any[]

  switch (dataKey) {
    case "mediaTypeSemantics":
      cellOptions = mediaSemanticOptions || []
      break
    case "vendorProductIds":
      cellOptions = vendorProductsOptions(row.value) || []
      break
    default:
      cellOptions = []
  }

  const selectedValues =
    (value as Array<any>)?.map((v) => ({
      name: cellOptions?.find((o) => o?.value === v)?.name,
      value: v,
    })) ?? []

  const authDisabled = !hasAuthority([
    "AUTH_EDIT_FEE_AGENCY",
    "AUTH_EDIT_FEE_CLIENT",
    "AUTH_EDIT_FEE_SUPPLIER",
  ])

  const conditionDisabled =
    dataKey === "vendorProductIds" && !hasMediaTypes(row.value)

  return open ? (
    <MultiSelect
      items={cellOptions}
      getOptionLabel={(option: Record<string, string>) => option?.name}
      selectedValues={selectedValues || []}
      checkValues={() => true}
      renderOption={(option: Record<string, string>) => option?.name}
      selectAllOption={{ name: t("filters.all"), value: "select-all" }}
      selectAllValue="select-all"
      onToggleOption={handleToggleOption}
      onClearOptions={handleClearOptions}
      onSelectAll={handleSelectAll}
      onBlur={onBlur}
      disabled={conditionDisabled}
      autofocus
      inputClassName={styles.mediatypeWidth}
      paperClassName={styles.paper}
    />
  ) : (
    <MoreButtonBlock
      options={selectedValues}
      error={error}
      onOpen={onOpen}
      dataKey={dataKey}
      name="fees"
      disabled={conditionDisabled || authDisabled || disabled}
      slice={1}
    />
  )
}

const styles = {
  readonly: css`
    margin: var(--spacing-m);
  `,
  input: css`
    font-size: 13px;
    :hover {
      cursor: pointer;
      width: 100%;
    }
    .MuiOutlinedInput-input {
      font-size: 13px;
      text-align: right;
    }
  `,
  prcInput: css`
    .MuiOutlinedInput-input {
      padding-right: 0;
    }
  `,
  ghostInput: css`
    :focus-within {
      border: 2px solid var(--primary-main, teal);
      width: 100%;
    }
  `,
  ghostSelect: css`
    align-items: center;
    border: none;
    width: 100%;

    outline: none;

    .MuiSvgIcon-root {
      display: none;
    }

    :not([data-disabled="true"]):hover {
      border-color: var(--primary-main, teal);
      .MuiSvgIcon-root {
        display: block;
      }
    }

    :focus,
    :focus-within {
      width: 100%;
      border-color: var(--primary-main, teal);
      .MuiSvgIcon-root {
        display: block;
      }
    }

    .MuiInputBase-root {
      font-size: 13px;
      width: 100%;
    }
    .MuiOutlinedInput-notchedOutline {
      border: none;
    }
    .MuiSelect-outlined.MuiSelect-outlined {
      padding-top: var(--spacing-xs);
      padding-bottom: var(--spacing-xs);
    }
  `,
  buttonContainer: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  `,
  error: css`
    border: 1px solid var(--red);
  `,
  mediatypeWidth: css`
    width: 100%;
  `,
  paper: css`
    text-transform: capitalize;
  `,
}

const commonColumns = (disableEdit: boolean, isScopeVendor?: boolean) => [
  {
    id: "1",
    Header: "label",
    dataKey: "label",
    Cell: (props: CellRendererProps) => (
      <EditableCell {...props} disabled={disableEdit} />
    ),
  },
  {
    id: "2",
    Header: "computed on",
    dataKey: "computedOn",
    Cell: (props: CellRendererProps) => (
      <SelectCell {...props} disabled={disableEdit} />
    ),
  },
  {
    id: "3",
    Header: "semantic",
    dataKey: "semantic",
    Cell: (props: CellRendererProps) => (
      <SelectCell
        {...props}
        isScopeVendor={isScopeVendor}
        disabled={disableEdit}
      />
    ),
  },
  {
    id: "4",
    Header: "type",
    dataKey: "amountType",
    Cell: (props: CellRendererProps) => (
      <SelectCell {...props} disabled={disableEdit} />
    ),
  },
  {
    id: "5",
    Header: "amount",
    dataKey: "amount",
    Cell: (props: CellRendererProps) => (
      <EditableNumberCell {...props} disabled={disableEdit} />
    ),
  },
  {
    id: "6",
    Header: "media type",
    dataKey: "mediaTypeSemantics",
    Cell: (props: CellRendererProps) => (
      <MultipleSelectCell {...props} disabled={disableEdit} />
    ),
  },
]

const vendorColumn = (disableEdit?: boolean) => [
  {
    id: "7",
    Header: "vendor product",
    dataKey: "vendorProductIds",
    Cell: (props: CellRendererProps) => (
      <MultipleSelectCell {...props} disabled={disableEdit} />
    ),
  },
]

const actionColumn = [
  {
    id: "8",
    Header: "actions",
    dataKey: "",
    width: 80,
    Cell: function (props: CellRendererProps) {
      const { t } = useI18n()
      const { onDelete } = useContextOptions()
      const { rowId } = props
      const row = useRow<Fee>(rowId)
      const { hasAuthority } = useAuthenticated()

      return (
        <MoreActions
          options={[
            // DELETE
            {
              hidden: !hasAuthority([
                "AUTH_DELETE_FEE_AGENCY",
                "AUTH_DELETE_FEE_CLIENT",
                "AUTH_DELETE_FEE_SUPPLIER",
              ]),
              label: t("vendor.fees.list.delete.action"),
              icon: <DeleteIcon />,
              onClick: () => {
                onDelete(row.value)
              },
            },
          ]}
        />
      )
    },
  },
]

export const mergedColumns = (
  allowed: boolean,
  disableEdit: boolean,
  isScopeVendor?: boolean,
) => {
  return allowed
    ? [
        ...commonColumns(disableEdit, isScopeVendor).slice(0, 6),
        ...vendorColumn(disableEdit),
        ...commonColumns(disableEdit, isScopeVendor).slice(6),
      ]
    : commonColumns(disableEdit, isScopeVendor)
}

export const columns = (
  allowed: boolean,
  disableAction: boolean,
  disableEdit: boolean,
  isScopeVendor?: boolean,
) => {
  return !disableAction
    ? [...mergedColumns(allowed, disableEdit, isScopeVendor), ...actionColumn]
    : mergedColumns(allowed, disableEdit, isScopeVendor)
}
